import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import { useAlerts, useAllDocuments, type TenantDocument } from '../lib/hooks';
import { Avatar, Button, Card, EmptyState, PageHeader, Pill, Skeleton } from '../components/ui';
import { InfoNote, TabStrip, docTone } from '../components/compliance';
import { ArrowUpRight, Bell, CalendarClock, FileText, RefreshCw, TriangleAlert } from 'lucide-react';
import type { Alert } from '../lib/phase1';
import { ScreeningBatchPanel } from './ScreeningBatch';
import { VigilanciaRegistralPanel } from './RegistroPublico';

/** Pestañas de la bandeja unificada. El valor vive en ?tab= para ser enlazable. */
const TABS = ['alertas', 'validacion-masiva', 'vigilancia-registral'] as const;
type AlertasTab = (typeof TABS)[number];

const initials = (name: string) =>
  name.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

/** Maroon-harmonized tone for an alert priority. */
function prioridadTone(prioridad: Alert['prioridad']) {
  return prioridad === 'Alta' ? 'red' : prioridad === 'Media' ? 'amber' : 'slate';
}

/** Maroon-harmonized tone for an alert estado (free-form backend string). */
function estadoTone(estado: string) {
  if (estado.includes('Escalada')) return 'red';
  if (estado.includes('Desestimada')) return 'slate';
  if (estado.includes('Resuelta')) return 'green';
  if (estado === 'Generada') return 'slate';
  return 'blue';
}

/** Section header row shared by the two tables (icon + title + count pill + optional action). */
function SectionHead({
  icon,
  title,
  count,
  action,
}: {
  icon: ReactNode;
  title: string;
  count?: number | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="mb-2.5 flex flex-wrap items-center gap-2">
      <span className="text-primary-dark">{icon}</span>
      <h2 className="font-serif text-base font-medium text-ink">{title}</h2>
      {count !== undefined && (
        <span className="rounded-full bg-canvas px-2 py-0.5 font-mono text-[10px] text-ink-faint">
          {count}
        </span>
      )}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

/**
 * «Documentos por vencer» — the tenant-wide catalog filtered to documents that are
 * expiring or already expired. Read-only summary table; a row jumps to the client's
 * revisión, and "Ver todos" opens the full Documentos catalog pre-filtered.
 */
function DocumentosPorVencerPanel() {
  const navigate = useNavigate();
  const { data: allDocs, isLoading, isError } = useAllDocuments();

  const docs = useMemo(
    () => (allDocs ?? []).filter((d) => d.estado === 'Por Vencer' || d.estado === 'Vencido'),
    [allDocs]
  );

  return (
    <div className="mb-4">
      <SectionHead
        icon={<CalendarClock className="h-4 w-4" strokeWidth={2} />}
        title="Documentos por vencer"
        count={allDocs !== undefined ? docs.length : undefined}
        action={
          <Link
            to="/documentos?filter=por-vencer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-dark hover:underline"
          >
            Ver todos
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </Link>
        }
      />

      {isLoading ? (
        <Card>
          <div className="grid gap-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudieron cargar los documentos"
            hint="Verifica tu conexión e inténtalo de nuevo."
          />
        </Card>
      ) : docs.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="h-8 w-8" strokeWidth={1.5} />}
            title="Ningún documento por vencer"
            hint="Todos los documentos de los expedientes están vigentes."
          />
        </Card>
      ) : (
        <>
          {/* mobile: stacked cards (no horizontal scroll) */}
          <div className="grid gap-2.5 sm:hidden">
            {docs.map((d: TenantDocument) => (
              <button
                key={d.id}
                onClick={() => navigate(`/revision/${d.expId}`)}
                className="w-full rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-colors hover:border-ink-faint"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-ink">{d.name}</span>
                  <Pill tone={docTone(d.estado)} className="shrink-0">
                    {d.estado}
                  </Pill>
                </div>
                <div className="mt-1 text-sm text-ink-soft">{d.cliente}</div>
                <div className="mt-1 font-mono text-[11px] text-ink-faint">
                  {d.category}
                  {d.expiry ? ` · vence ${d.expiry}` : ''}
                </div>
              </button>
            ))}
          </div>

          {/* desktop: table */}
          <Card className="hidden overflow-x-auto p-0 sm:block">
            <table className="w-full min-w-[40rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left">
                  {['Documento', 'Cliente', 'Categoría', 'Vence', 'Estado'].map((h) => (
                    <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((d: TenantDocument) => (
                  <tr
                    key={d.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/revision/${d.expId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/revision/${d.expId}`);
                      }
                    }}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                  >
                    <td className="px-5 py-3 font-bold">{d.name}</td>
                    <td className="px-5 py-3 text-ink-soft">{d.cliente}</td>
                    <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{d.category}</td>
                    <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{d.expiry ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Pill tone={docTone(d.estado)}>{d.estado}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export default function Alertas() {
  const navigate = useNavigate();
  const { data: alerts, isLoading, isError, refetch } = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('tab');
  const tab: AlertasTab = (TABS as readonly string[]).includes(raw ?? '')
    ? (raw as AlertasTab)
    : 'alertas';
  const goTo = (key: string) => setSearchParams(key === 'alertas' ? {} : { tab: key });

  return (
    <div>
      <PageHeader
        title="Alertas y Casos"
        subtitle="Bandeja unificada del motor de reglas, la validación masiva y la vigilancia registral"
      />

      <TabStrip
        active={tab}
        onChange={goTo}
        tabs={[
          { key: 'alertas', label: 'Alertas y casos', ...(alerts ? { count: alerts.length } : {}) },
          { key: 'validacion-masiva', label: 'Validación masiva' },
          { key: 'vigilancia-registral', label: 'Vigilancia Registral' },
        ]}
      />

      {tab === 'validacion-masiva' && <ScreeningBatchPanel />}
      {tab === 'vigilancia-registral' && (
        <VigilanciaRegistralPanel onGoToValidacion={() => goTo('validacion-masiva')} />
      )}

      {tab === 'alertas' && (
        <>
      <div className="mb-4">
        <SectionHead
          icon={<Bell className="h-4 w-4" strokeWidth={2} />}
          title="Alertas"
          count={alerts !== undefined ? (alerts ?? []).length : undefined}
        />

        {isLoading ? (
          <Card>
            <div className="grid gap-2.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          </Card>
        ) : isError ? (
          <Card>
            <EmptyState
              icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
              title="No se pudieron cargar las alertas"
              hint="Ocurrió un error al consultar la bandeja. Vuelve a intentarlo en unos momentos."
              action={<Button onClick={() => void refetch()}>Reintentar</Button>}
            />
          </Card>
        ) : (alerts ?? []).length === 0 ? (
          <Card>
            <EmptyState
              icon={<Bell className="h-8 w-8" strokeWidth={1.5} />}
              title="Sin alertas abiertas"
              hint="El motor de reglas y el monitoreo continuo generarán alertas aquí automáticamente."
            />
          </Card>
        ) : (
          <>
            {/* mobile: stacked cards (no horizontal scroll) */}
            <div className="grid gap-2.5 sm:hidden">
              {(alerts ?? []).map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/alertas/${a.id}`)}
                  className="w-full rounded-2xl border border-line bg-panel p-4 text-left shadow-card transition-colors hover:border-ink-faint"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-bold text-ink">{a.tipo}</span>
                    <Pill tone={prioridadTone(a.prioridad)}>{a.prioridad}</Pill>
                  </div>
                  <div className="mt-1 text-sm text-ink-soft">{a.cliente}</div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Pill tone={estadoTone(a.estado)}>{a.estado}</Pill>
                    {a.asignada ? (
                      <span className="flex items-center gap-1.5 text-sm text-ink-soft">
                        <Avatar initials={initials(a.asignada)} />
                        {a.asignada}
                      </span>
                    ) : (
                      <span className="text-sm text-ink-faint">Sin asignar</span>
                    )}
                    <span className="ml-auto font-mono text-[10px] text-ink-faint">{a.id}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* desktop: table */}
            <Card data-tour="alertas-table" className="hidden overflow-x-auto p-0 sm:block">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b border-line text-left">
                    {['Alerta', 'Tipo', 'Cliente', 'Prioridad', 'Estado', 'Asignada'].map((h) => (
                      <th key={h} className="px-5 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(alerts ?? []).map((a) => (
                    <tr
                      key={a.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/alertas/${a.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/alertas/${a.id}`);
                        }
                      }}
                      className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                    >
                      <td className="px-5 py-3 font-mono text-[11px] text-ink-faint">{a.id}</td>
                      <td className="px-5 py-3 font-bold">{a.tipo}</td>
                      <td className="px-5 py-3 text-ink-soft">{a.cliente}</td>
                      <td className="px-5 py-3">
                        <Pill tone={prioridadTone(a.prioridad)}>{a.prioridad}</Pill>
                      </td>
                      <td className="px-5 py-3">
                        <Pill tone={estadoTone(a.estado)}>{a.estado}</Pill>
                      </td>
                      <td className="px-5 py-3">
                        {a.asignada ? (
                          <span className="flex items-center gap-2">
                            <Avatar initials={initials(a.asignada)} />
                            <span className="text-ink-soft">{a.asignada}</span>
                          </span>
                        ) : (
                          <span className="text-ink-faint">Sin asignar</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>

      <DocumentosPorVencerPanel />

      <InfoNote tone="blue">
        <RefreshCw className="h-5 w-5 shrink-0" strokeWidth={1.75} />
        <div>
          El re-screening continuo, las renovaciones de debida diligencia y los watchers de
          vencimiento generan alertas automáticamente. Cada descarte o escalamiento exige una razón
          y queda en la bitácora inmutable.
        </div>
      </InfoNote>
        </>
      )}
    </div>
  );
}
